import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  PlayActiveLightIcon,
  PauseActiveLightIcon,
  SpeakerIcon,
} from "../../../../assets/icon";
import { Maximize2, Minimize2, VolumeX } from "lucide-react";
import { ReactionAndFavorite } from "../reaction-and-favorite/reaction-and-favorite";
import { videoPlayerStyles } from "./styles";
import { isMobile } from "react-device-detect";
import { useMessageInteractionTracking } from "../../../../hooks/useMessageInteractionTracking";
import { sendAudioListeningTime } from "../../../../axios/user";
import useUserStore from "../../../../stores/userStore";
import { useAuthenticatedFileUrl } from "../../../../hooks/useAuthenticatedFileUrl";

// Detect iOS Safari
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isIOSSafari = isIOS || (isSafari && isMobile);

interface HTMLVideoElementWithWebkit extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
}

interface VideoPlayerProps {
  videoUrl: string;
  isBot: boolean;
  title?: string;
  currentReaction?: string;
  messageId: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  // When true, hide ReactionAndFavorite (used inside TabbedMessage)
  embedded?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  isBot,
  title,
  currentReaction,
  messageId,
  isSaved,
  markedIds,
  isTags,
  handleRemoveTag,
  handleReactionClick,
  handleAddToFavorite,
  embedded = false,
}) => {
  const { url: resolvedVideoUrl, isLoading: isResolvingVideoUrl } =
    useAuthenticatedFileUrl(videoUrl);
  const effectiveVideoUrl = resolvedVideoUrl ?? "";
  const theme = useTheme();
  const styles = videoPlayerStyles(theme, !!title);
  const isDark = theme.palette.mode === "dark";
  const controlsIconColor = isDark ? "#fff" : "#fff";
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const volumeSliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingTimeRef = useRef<number>(0);
  const { user } = useUserStore();
  const { trackInteraction } = useMessageInteractionTracking(messageId);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [bufferedPercentage, setBufferedPercentage] = useState(0);
  const playStartRef = useRef<number | null>(null);
  const wasPlayingBeforeFullscreenRef = useRef<boolean>(false);
  const videoTimeBeforeFullscreenRef = useRef<number>(0);

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const s = Math.max(0, Math.round(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!videoRef.current || !effectiveVideoUrl) return;

    const video = videoRef.current;
    let firstFrameSeeked = false;

    const handleSeeked = () => {
      if (isIOSSafari && !firstFrameSeeked && video.paused) {
        firstFrameSeeked = true;
        setIsLoading(false);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoaded(true);
      setIsLoading(false);
      setError(null);

      if (isIOSSafari && video.readyState >= 2) {
        const wasPaused = video.paused;
        if (!wasPaused) {
          video.pause();
        }
        video.currentTime = 0.1;
        firstFrameSeeked = false;
      }
    };

    const handleLoadedData = () => {
      setIsLoading(false);

      if (isIOSSafari && video.readyState >= 2 && video.paused) {
        if (video.currentTime === 0 || video.currentTime < 0.05) {
          video.currentTime = 0.1;
          firstFrameSeeked = false;
        }
      }
    };

    const handleTimeUpdate = () => {
      if (!isDragging && video) {
        const newTime = video.currentTime;
        if (isFinite(newTime)) {
          setCurrentTime(newTime);
        }
      }
    };

    const handlePlay = () => {
      if (video && !video.paused) {
        setIsPlaying(true);
        if (isMobile) {
          setTimeout(() => {
            setShowControls(false);
          }, 500);
        }
      }

      if (
        video.currentTime === 0.1 &&
        !isFullscreen &&
        !wasPlayingBeforeFullscreenRef.current &&
        playStartRef.current === null
      ) {
        video.currentTime = 0;
        setCurrentTime(0);
      }
      if (!playStartRef.current) {
        const startTime = Date.now();
        playStartRef.current = startTime;
        trackInteraction("video_start", 0);
      }
    };

    const handlePause = () => {
      if (video && video.paused) {
        setIsPlaying(false);
        videoTimeBeforeFullscreenRef.current = video.currentTime;
        if (isMobile) {
          setShowControls(true);
        }
      }
      if (playStartRef.current && user?.id) {
        const watched = (Date.now() - playStartRef.current) / 1000;
        sendAudioListeningTime(user.id, messageId, watched).catch(
          console.error,
        );
        trackInteraction("video_pause", watched);
        playStartRef.current = null;
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (playStartRef.current && user?.id) {
        const watched = (Date.now() - playStartRef.current) / 1000;
        sendAudioListeningTime(user.id, messageId, watched).catch(
          console.error,
        );
        trackInteraction("video_play", watched);
        playStartRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    };

    const handleError = () => {
      setIsLoading(false);
      setError("Failed to load video");
      setIsPlaying(false);
    };

    const handleWaiting = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);

      if (isIOSSafari && video.paused && video.readyState >= 2) {
        if (video.currentTime === 0 || video.currentTime < 0.05) {
          video.currentTime = 0.1;
          firstFrameSeeked = false;
        }
      }

      if (
        video.duration > 0 &&
        video.paused &&
        video.currentTime === 0 &&
        playStartRef.current === null &&
        !isIOSSafari
      ) {
        video.currentTime = 0.1;
      }
    };

    const handleProgress = () => {
      if (video && video.buffered.length > 0 && video.duration > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / video.duration) * 100;
        setBufferedPercentage(bufferedPercent);
      }
    };

    const handleVolumeChange = () => {
      if (video) {
        setVolume(video.volume);
        setIsMuted(video.muted);
      }
    };

    const handleFullscreenChange = async () => {
      let newIsFullscreen = false;
      if (isIOSSafari && video) {
        const videoWithWebkit = video as HTMLVideoElementWithWebkit;
        newIsFullscreen = !!videoWithWebkit.webkitDisplayingFullscreen;
      } else {
        newIsFullscreen = !!(
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement
        );
      }
      setIsFullscreen(newIsFullscreen);

      if (video) {
        if (video.playbackRate !== playbackRate) {
          video.playbackRate = playbackRate;
        }

        const savedTime =
          videoTimeBeforeFullscreenRef.current || video.currentTime;

        setTimeout(() => {
          if (videoRef.current) {
            const currentVideo = videoRef.current;
            if (Math.abs(currentVideo.currentTime - savedTime) > 0.1) {
              currentVideo.currentTime = savedTime;
            }
            if (currentVideo.playbackRate !== playbackRate) {
              currentVideo.playbackRate = playbackRate;
            }
            videoTimeBeforeFullscreenRef.current = currentVideo.currentTime;
          }
        }, 50);

        const isVideoPlaying = !video.paused;
        setIsPlaying(isVideoPlaying);

        if (!newIsFullscreen && video.paused) {
          setIsPlaying(false);
          setShowControls(true);
        }
      }
    };

    const handleWebkitBeginFullscreen = () => {
      setIsFullscreen(true);
      if (videoRef.current) {
        const video = videoRef.current;
        if (video.playbackRate !== playbackRate) {
          video.playbackRate = playbackRate;
        }
        setTimeout(() => {
          if (
            videoRef.current &&
            videoRef.current.playbackRate !== playbackRate
          ) {
            videoRef.current.playbackRate = playbackRate;
          }
        }, 100);
      }
    };

    const handleWebkitEndFullscreen = () => {
      setIsFullscreen(false);
      if (videoRef.current) {
        const video = videoRef.current;
        if (video.playbackRate !== playbackRate) {
          video.playbackRate = playbackRate;
        }
        setTimeout(() => {
          if (
            videoRef.current &&
            videoRef.current.playbackRate !== playbackRate
          ) {
            videoRef.current.playbackRate = playbackRate;
          }
        }, 100);
        if (video.paused) {
          setIsPlaying(false);
          setShowControls(true);
        }
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("error", handleError);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("canplaythrough", handleCanPlay);
    video.addEventListener("progress", handleProgress);
    video.addEventListener("volumechange", handleVolumeChange);
    video.addEventListener("seeked", handleSeeked);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    if (isIOSSafari) {
      video.addEventListener(
        "webkitbeginfullscreen",
        handleWebkitBeginFullscreen,
      );
      video.addEventListener("webkitendfullscreen", handleWebkitEndFullscreen);
    }

    if (video.volume !== volume) {
      video.volume = volume;
    }
    if (video.muted !== isMuted) {
      video.muted = isMuted;
    }
    if (video.playbackRate !== playbackRate) {
      video.playbackRate = playbackRate;
    }

    if (isMobile) {
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.setAttribute("x5-playsinline", "true");
    }

    if (isIOSSafari) {
      const showFirstFrame = () => {
        if (video.readyState >= 2 && video.paused) {
          const currentTime = video.currentTime;
          if (currentTime === 0 || isNaN(currentTime) || currentTime < 0.05) {
            video.currentTime = 0.1;
          }
        }
      };

      if (video.readyState >= 1) {
        setTimeout(showFirstFrame, 100);
      } else {
        const handleOnce = () => {
          setTimeout(showFirstFrame, 100);
        };
        video.addEventListener("loadedmetadata", handleOnce, { once: true });
        video.addEventListener("loadeddata", handleOnce, { once: true });
        video.addEventListener("canplay", handleOnce, { once: true });
      }
    }

    if (video.src !== effectiveVideoUrl) {
      video.load();
    }

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handleError);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("canplaythrough", handleCanPlay);
      video.removeEventListener("progress", handleProgress);
      video.removeEventListener("volumechange", handleVolumeChange);
      video.removeEventListener("seeked", handleSeeked);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );

      if (isIOSSafari) {
        video.removeEventListener(
          "webkitbeginfullscreen",
          handleWebkitBeginFullscreen,
        );
        video.removeEventListener(
          "webkitendfullscreen",
          handleWebkitEndFullscreen,
        );
      }
    };
  }, [
    effectiveVideoUrl,
    isDragging,
    volume,
    isMuted,
    playbackRate,
    messageId,
    user?.id,
    trackInteraction,
    isFullscreen,
  ]);

  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isLoaded) return;

    try {
      if (isPlaying) {
        video.pause();
        setIsPlaying(false);
        videoTimeBeforeFullscreenRef.current = video.currentTime;
        if (isMobile) {
          setShowControls(true);
        }
      } else {
        const timeBeforePlay = video.currentTime;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
              if (Math.abs(video.currentTime - timeBeforePlay) > 0.5) {
                video.currentTime = timeBeforePlay;
              }
              if (isMobile) {
                setTimeout(() => {
                  setShowControls(false);
                }, 500);
              }
            })
            .catch((err) => {
              console.error("Error playing video:", err);
              setError("Failed to play video");
              setIsPlaying(false);
            });
        } else {
          setIsPlaying(!video.paused);
          if (isMobile && !video.paused) {
            setTimeout(() => {
              setShowControls(false);
            }, 500);
          }
        }
      }
    } catch (err) {
      console.error("Error toggling play/pause:", err);
      setIsPlaying(false);
    }
  }, [isPlaying, isLoaded]);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (
        !videoRef.current ||
        !progressBarRef.current ||
        !isLoaded ||
        !duration
      )
        return;

      e.preventDefault();
      e.stopPropagation();

      const video = videoRef.current;
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const newTime = percentage * duration;

      if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
        const wasPaused = video.paused;
        video.currentTime = newTime;
        setCurrentTime(newTime);
        if (wasPaused) {
          setTimeout(() => {
            if (video && video.paused !== wasPaused) {
              video.pause();
            }
          }, 0);
        }
      }
    },
    [duration, isLoaded],
  );

  const handleProgressMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (
        !videoRef.current ||
        !progressBarRef.current ||
        !isLoaded ||
        !duration
      )
        return;

      e.preventDefault();
      e.stopPropagation();

      const video = videoRef.current;
      const wasPaused = video.paused;
      setIsDragging(true);

      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const newTime = percentage * duration;

      if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
        draggingTimeRef.current = newTime;
        setCurrentTime(newTime);
        video.currentTime = newTime;
        if (wasPaused && !video.paused) {
          video.pause();
        }
      }
    },
    [duration, isLoaded],
  );

  const handleProgressTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (
        !videoRef.current ||
        !progressBarRef.current ||
        !isLoaded ||
        !duration
      )
        return;

      e.preventDefault();
      e.stopPropagation();

      const video = videoRef.current;
      const wasPaused = video.paused;
      setIsDragging(true);

      const rect = progressBarRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      const clickX = touch.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const newTime = percentage * duration;

      if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
        draggingTimeRef.current = newTime;
        setCurrentTime(newTime);
        video.currentTime = newTime;
        if (wasPaused && !video.paused) {
          video.pause();
        }
      }
    },
    [duration, isLoaded],
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (
        !videoRef.current ||
        !progressBarRef.current ||
        !isLoaded ||
        !duration
      )
        return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const newTime = percentage * duration;

      if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
        draggingTimeRef.current = newTime;
        setCurrentTime(newTime);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (
        !videoRef.current ||
        !progressBarRef.current ||
        !isLoaded ||
        !duration
      )
        return;

      e.preventDefault();
      const rect = progressBarRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      const clickX = touch.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const newTime = percentage * duration;

      if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
        draggingTimeRef.current = newTime;
        setCurrentTime(newTime);
      }
    };

    const handleMouseUp = () => {
      const video = videoRef.current;
      if (!video || !isLoaded) return;

      const finalTime = draggingTimeRef.current;
      if (isFinite(finalTime) && finalTime >= 0 && finalTime <= duration) {
        const wasPaused = video.paused;
        video.currentTime = finalTime;
        setCurrentTime(finalTime);
        if (wasPaused) {
          setTimeout(() => {
            if (video && !video.paused) {
              video.pause();
            }
          }, 0);
        }
      }
      setIsDragging(false);
    };

    const handleTouchEnd = () => {
      handleMouseUp();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mouseleave", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
    document.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseleave", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isDragging, duration, isLoaded]);

  const handleVolumeClick = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  }, []);

  const handleVolumeSliderChange = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!videoRef.current || !volumeSliderRef.current) return;

      const rect = volumeSliderRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newVolume = Math.max(0, Math.min(1, clickX / width));

      videoRef.current.volume = newVolume;
      videoRef.current.muted = newVolume === 0;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    },
    [],
  );

  const handleFullscreen = useCallback(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    wasPlayingBeforeFullscreenRef.current = !video.paused;
    videoTimeBeforeFullscreenRef.current = video.currentTime;

    try {
      if (isIOSSafari) {
        const videoWithWebkit = video as HTMLVideoElementWithWebkit;
        if (video.playbackRate !== playbackRate) {
          video.playbackRate = playbackRate;
        }
        if (videoWithWebkit.webkitEnterFullscreen) {
          videoWithWebkit.webkitEnterFullscreen();
        } else if (videoWithWebkit.webkitDisplayingFullscreen) {
          if ((document as any).webkitExitFullscreen) {
            (document as any).webkitExitFullscreen();
          }
        }
        return;
      }

      if (!containerRef.current) return;

      if (!isFullscreen) {
        const promise = containerRef.current.requestFullscreen
          ? containerRef.current.requestFullscreen()
          : (containerRef.current as any).webkitRequestFullscreen
            ? (containerRef.current as any).webkitRequestFullscreen()
            : (containerRef.current as any).mozRequestFullScreen
              ? (containerRef.current as any).mozRequestFullScreen()
              : (containerRef.current as any).msRequestFullscreen
                ? (containerRef.current as any).msRequestFullscreen()
                : null;

        if (promise && typeof promise.then === "function") {
          promise.then(() => {
            if (videoRef.current) {
              const currentVideo = videoRef.current;
              const savedTime = videoTimeBeforeFullscreenRef.current;
              if (Math.abs(currentVideo.currentTime - savedTime) > 0.1) {
                currentVideo.currentTime = savedTime;
              }
              if (currentVideo.playbackRate !== playbackRate) {
                currentVideo.playbackRate = playbackRate;
              }
            }
          });
        }
      } else {
        const promise = document.exitFullscreen
          ? document.exitFullscreen()
          : (document as any).webkitExitFullscreen
            ? (document as any).webkitExitFullscreen()
            : (document as any).mozCancelFullScreen
              ? (document as any).mozCancelFullScreen()
              : (document as any).msExitFullscreen
                ? (document as any).msExitFullscreen()
                : null;

        if (promise && typeof promise.then === "function") {
          promise.then(() => {
            if (videoRef.current) {
              const currentVideo = videoRef.current;
              const savedTime = videoTimeBeforeFullscreenRef.current;
              if (Math.abs(currentVideo.currentTime - savedTime) > 0.1) {
                currentVideo.currentTime = savedTime;
              }
            }
          });
        }
      }
    } catch (err) {
      console.error("Error toggling fullscreen:", err);
    }
  }, [isFullscreen, playbackRate]);

  useEffect(() => {
    if (isMobile && isPlaying && showControls) {
      const timer = setTimeout(() => {
        setShowControls(false);
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (!isMobile && showControls) {
      const timer = setTimeout(() => {
        setShowControls(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isPlaying, showControls, currentTime]);

  useEffect(() => {
    if (!isFullscreen && videoRef.current) {
      const timeoutId = setTimeout(() => {
        if (videoRef.current) {
          const currentVideo = videoRef.current;
          const isVideoPaused = currentVideo.paused;
          setIsPlaying(!isVideoPaused);
          if (isVideoPaused) {
            setShowControls(true);
            setIsLoading(false);
          }
        }
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [isFullscreen]);

  useEffect(() => {
    if (videoRef.current && isFullscreen) {
      const video = videoRef.current;
      if (video.playbackRate !== playbackRate) {
        video.playbackRate = playbackRate;
      }
    }
  }, [playbackRate, isFullscreen]);

  const handleVideoInteraction = useCallback(() => {
    setShowControls(true);
    if (isMobile && isPlaying) {
      setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }
  }, [isPlaying]);

  const handleVideoMouseLeave = useCallback(() => {
    if (!isMobile && isPlaying) {
      setTimeout(() => {
        setShowControls(false);
      }, 1000);
    }
  }, [isPlaying]);

  const handleVideoClick = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    ) => {
      const target = e.target as HTMLElement;
      if (
        target.closest("[data-controls]") ||
        target.closest("[data-center-play-button]")
      ) {
        return;
      }
      handlePlayPause();
    },
    [handlePlayPause],
  );

  const handleSpeedChange = useCallback((e: SelectChangeEvent<number>) => {
    const rate = Number(e.target.value);
    setPlaybackRate(rate);
    if (videoRef.current) {
      const video = videoRef.current;
      const wasPlaying = !video.paused;
      video.playbackRate = rate;
      if (wasPlaying && video.paused) {
        video.play().catch(console.error);
      }
    }
  }, []);

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  const volumePercentage = volume * 100;

  const speedSelectMenuProps = useMemo(
    () => ({
      anchorOrigin: {
        vertical: "top" as const,
        horizontal: "left" as const,
      },
      transformOrigin: {
        vertical: "bottom" as const,
        horizontal: "left" as const,
      },
      disablePortal: isFullscreen,
      ...(isFullscreen && {
        container: containerRef.current || undefined,
      }),
      PaperProps: {
        sx: {
          zIndex: isFullscreen ? 9999 : undefined,
          maxHeight: 200,
        },
      },
    }),
    [isFullscreen],
  );

  if (isResolvingVideoUrl || !effectiveVideoUrl) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...styles.container,
        ...(isFullscreen && {
          borderRadius: 0,
          width: "100vw",
          height: "100vh",
          maxWidth: "100vw",
          backgroundColor: "#000",
          justifyContent: "center",
          alignItems: "center",
        }),
      }}
      ref={containerRef}
    >
      {title && !isFullscreen && (
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography sx={styles.title}>{title}</Typography>
        </Box>
      )}

      <Box
        sx={{
          ...styles.videoWrapper,
          ...(isFullscreen && {
            width: "100vw",
            height: "100vh",
            minHeight: "100vh",
          }),
        }}
        onMouseMove={handleVideoInteraction}
        onMouseLeave={handleVideoMouseLeave}
        onTouchStart={handleVideoInteraction}
        onClick={handleVideoClick}
      >
        <video
          ref={videoRef}
          src={effectiveVideoUrl}
          poster={title ? "" : undefined}
          style={{
            ...styles.video,
            objectFit: "contain" as const,
            margin: 0,
            padding: 0,
            display: "block",
            backgroundColor: "#000",
            ...(isFullscreen && {
              width: "100vw",
              height: "100vh",
              maxHeight: "100vh",
            }),
          }}
          playsInline
          preload={isIOSSafari ? "auto" : "metadata"}
          controls={false}
          webkit-playsinline="true"
          x5-playsinline="true"
        />

        {isLoading && !error && (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={40} sx={{ color: "#06B6D4" }} />
          </Box>
        )}

        {error && (
          <Box sx={styles.errorContainer}>
            <Typography variant="body2">{error}</Typography>
            <Typography variant="caption" sx={{ mt: 1, opacity: 0.7 }}>
              Please try again later
            </Typography>
          </Box>
        )}

        {!isPlaying && !isLoading && !error && isLoaded && (
          <Box
            sx={styles.centerPlayButton}
            onClick={handlePlayPause}
            data-center-play-button
          >
            <PlayActiveLightIcon
              width={isMobile ? 32 : 48}
              height={isMobile ? 32 : 48}
            />
          </Box>
        )}

        {showControls && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              width: "100%",
            }}
          >
            <Box sx={styles.controlsContainer} data-controls>
              <Box sx={styles.playButton} onClick={handlePlayPause}>
                {isPlaying ? (
                  <PauseActiveLightIcon width={20} height={20} />
                ) : (
                  <PlayActiveLightIcon width={20} height={20} />
                )}
              </Box>

              <Box sx={styles.timeDisplay}>
                <Typography variant="caption" sx={{ fontSize: "12px" }}>
                  {formatTime(currentTime)}
                </Typography>
              </Box>

              <Box sx={styles.progressContainer}>
                <Box
                  ref={progressBarRef}
                  sx={styles.progressBar}
                  onClick={handleProgressClick}
                  onMouseDown={handleProgressMouseDown}
                  onTouchStart={handleProgressTouchStart}
                >
                  <Box
                    sx={{
                      ...styles.bufferedBar,
                      width: `${bufferedPercentage}%`,
                    }}
                  />
                  <Box
                    sx={{
                      ...styles.progressFill,
                      width: `${progressPercentage}%`,
                    }}
                  />
                </Box>
              </Box>

              <Box sx={styles.timeDisplay}>
                <Typography variant="caption" sx={{ fontSize: "12px" }}>
                  {formatTime(duration)}
                </Typography>
              </Box>

              <Box sx={styles.volumeContainer}>
                <Box sx={styles.volumeButton} onClick={handleVolumeClick}>
                  {isMuted || volume === 0 ? (
                    <VolumeX size={18} style={{ color: controlsIconColor }} />
                  ) : (
                    <SpeakerIcon size={18} color={controlsIconColor} />
                  )}
                </Box>
                {!isMobile && (
                  <Box
                    ref={volumeSliderRef}
                    sx={{
                      ...styles.progressBar,
                      ...styles.volumeSlider,
                    }}
                    onClick={handleVolumeSliderChange}
                  >
                    <Box
                      sx={{
                        ...styles.progressFill,
                        width: `${volumePercentage}%`,
                      }}
                    />
                  </Box>
                )}
              </Box>

              <FormControl size="small" sx={{ minWidth: 50 }} data-controls>
                <Select
                  value={playbackRate}
                  onChange={handleSpeedChange}
                  disabled={!isLoaded}
                  sx={styles.speedSelect}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  MenuProps={speedSelectMenuProps}
                >
                  {[0.5, 1, 1.5, 2].map((v) => (
                    <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>
                      {v}x
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!isIOSSafari && (
                <Box
                  sx={styles.fullscreenButton}
                  onClick={handleFullscreen}
                  aria-label={
                    isFullscreen ? "Exit fullscreen" : "Enter fullscreen"
                  }
                >
                  {isFullscreen ? (
                    <Minimize2 size={18} color={controlsIconColor} />
                  ) : (
                    <Maximize2 size={18} color={controlsIconColor} />
                  )}
                </Box>
              )}
              {isIOSSafari && (
                <Box
                  sx={styles.fullscreenButton}
                  onClick={handleFullscreen}
                  aria-label="Enter fullscreen"
                >
                  <Maximize2 size={18} color={controlsIconColor} />
                </Box>
              )}
            </Box>
          </motion.div>
        )}
      </Box>

      {!isFullscreen && !embedded && (
        <ReactionAndFavorite
          currentReaction={currentReaction}
          markedIds={markedIds}
          isSaved={isSaved}
          isTags={isTags}
          handleReactionClick={handleReactionClick}
          handleAddToFavorite={handleAddToFavorite}
          handleRemoveTag={handleRemoveTag}
        />
      )}
    </Box>
  );
};

export default VideoPlayer;
